import React from 'react';
import { emptyObject } from '@constants/empty';
import {
  DPP_COMPLETED,
  DPP_INCOMPLETE,
  PRACTICE_FLOW_TYPE,
  PRACTICE_TYPE
} from '@cont/Practice/constants';
import useFinishSummary from '@stores/auto-dpp/dppFinishSummaryStore';
import usePracticeSegmentAnalytics from '@cont/Practice/usePracticeSegmentAnalytics';
import Image from '@base/Image';
import { useDispatch } from 'react-redux';
import { finishPracticeSession } from '@actions/practiceSession';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import usePracticeStore from '@stores/planner/practiceStore';
import PracticeByEducator from 'aquilla/core/organisms/Practice/PracticeByEducator';
import styled from '@emotion/styled';
import { Spacings, mediaQuery } from '@styles/index';
import { PRACTICE_STARTED } from '../constants';

const StyledPractiseCard = styled(PracticeByEducator)`
  &[class*='aquilla-practice-by-educator'] {
    width: 368px;

    hr + div {
      justify-content: space-between;
      > div {
        flex: unset;
      }

      ${mediaQuery.uptoMobile} {
        flex-direction: column-reverse;
        gap: ${Spacings.SPACING_4B};

        > div:nth-child(2) {
          width: 100%;
          > button {
            width: 100%;
          }
        }
      }
    }

    ${mediaQuery.uptoMobile} {
      width: calc(100vw - 80px);
    }
  }
`;

const DppCard = ({
  practice,
  isSubscribed,
  isLoggedIn = false,
  actionButton,
  as
}) => {
  const {
    classDetails,
    educatorDetails,
    sessionDetails,
    numQuestions,
    totalQuizAttempts,
    goalUid: goalUID,
    uid: quizUID,
    goalSlug
  } = practice ?? emptyObject;

  const router = useRouter();
  const { topicGroupName, className, topicGroupUid, topicColor } = classDetails;
  const { avatar, firstName, lastName } = educatorDetails ?? emptyObject;
  const { solvedQuestions, status, sessionUid } = sessionDetails ?? emptyObject;

  const { toggleShowSummary, setFinishSummary } = useFinishSummary();
  // @ts-ignore
  const { finishPlannerPracticeSession } = usePracticeStore();
  const segmentAnalytics = usePracticeSegmentAnalytics(goalUID);
  const dispatch = useDispatch();

  const isCompleted = status === DPP_COMPLETED;
  const isInComplete = status === DPP_INCOMPLETE;

  const handleButtonClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoggedIn || !isSubscribed) {
      return router.push(
        {
          pathname: '/goal/[goalSlug]/[goalUID]/subscribe'
        },
        `/goal/${goalSlug}/${goalUID}/subscribe`
      );
    }

    if (isCompleted && sessionUid) {
      finishPlannerPracticeSession({}, false);
      await dispatch(finishPracticeSession(sessionUid));
      setFinishSummary({
        goalSlug,
        goalUID,
        sessionId: sessionUid,
        topicGroup: topicGroupUid,
        topicUID: topicGroupUid,
        quizUID,
        type: PRACTICE_FLOW_TYPE.AUTO_DPP,
        educatorName: `${firstName} ${lastName}`
      });
      return toggleShowSummary();
    }
    if (quizUID && topicGroupUid && goalUID && goalSlug) {
      return pushRoute({
        pathname: '/goal/[goalSlug]/[goalUID]/practice/[topicUID]',
        query: {
          goalSlug,
          goalUID,
          topic_type: PRACTICE_TYPE.EACH_TOPIC,
          type: PRACTICE_FLOW_TYPE.AUTO_DPP,
          topic_group: topicGroupUid,
          topicUID: topicGroupUid,
          quiz_uid: quizUID,
          educator_name: `${firstName} ${lastName}`,
          source: 'Educator Profile'
        }
      }).then(() =>
        segmentAnalytics(PRACTICE_STARTED, {
          topic_uid: null,
          practice_questions: numQuestions,
          topology_id: topicGroupUid,
          topology_name: 'NA',
          topology_level: 'NA',
          last_primary_source: 'Educator Profile',
          language: 'NA',
          practice_type: 'Educator Led Practice',
          is_completed: isCompleted ? 'Yes' : 'No'
        })
      );
    }
  };

  const getAttemptText = () => {
    if (isCompleted) {
      return `${solvedQuestions} of ${numQuestions} completed`;
    }
    if (isInComplete && sessionUid && solvedQuestions >= 1) {
      return `${solvedQuestions} of ${numQuestions} attempted`;
    }
    return `${numQuestions} questions`;
  };

  const getBtnLabel = () => {
    if (!isLoggedIn || !isSubscribed) {
      return 'locked';
    }
    if (isInComplete && sessionUid && solvedQuestions >= 1) {
      return 'resume';
    }
    return 'start';
  };

  return (
    <StyledPractiseCard
      as={as}
      EducatorImage={
        <Image alt="Educator" src={avatar} width="56" height="56" />
      }
      educatorName={`${firstName} ${lastName}`}
      href=""
      nAttempted={solvedQuestions}
      nPeopleAttempted={totalQuizAttempts}
      nTotal={numQuestions}
      state={getBtnLabel()}
      title={className}
      topicColor={topicColor}
      topicName={topicGroupName}
      onClick={handleButtonClick}
      attemptString={getAttemptText()}
      actionButton={actionButton}
    />
  );
};

export default DppCard;
